import { normalizeGoogleMapsEmbedUrl } from '@/utils/normalize-google-maps-embed-url';

const ALLOWED_TAGS =
  /^(p|span|strong|em|b|i|u|s|del|strike|sup|sub|ul|ol|li|br|a|img|h1|h2|h3|h4|h5|h6|div|video|iframe|blockquote|table|thead|tbody|tfoot|tr|th|td|hr|code|pre|figure|figcaption|input)$/i;

const DEFAULT_ATTRS = /^(class|style)$/i;

const ALLOWED_ATTRS: Record<string, RegExp> = {
  a: /^(href|title|target|rel|class|style)$/i,
  img: /^(src|alt|title|width|height|class|style)$/i,
  video: /^(src|width|height|controls|poster|preload|class|style)$/i,
  iframe: /^(src|width|height|title|allow|allowfullscreen|frameborder|class|style)$/i,
  input: /^(type|checked|disabled|class|style)$/i,
  td: /^(colspan|rowspan|class|style)$/i,
  th: /^(colspan|rowspan|class|style)$/i,
  table: /^(class|style)$/i,
  thead: /^(class|style)$/i,
  tbody: /^(class|style)$/i,
  tfoot: /^(class|style)$/i,
  tr: /^(class|style)$/i,
};

const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
]);

const ALLOWED_IFRAME_ORIGINS = [
  'https://www.youtube.com',
  'https://www.youtube-nocookie.com',
  'https://player.vimeo.com',
  'https://www.google.com',
  'https://maps.google.com',
];

const SAFE_CLASS_PATTERN = /^[\w-]+(?: [\w-]+)*$/;

export const toEmbedVideoSrc = (src: string): string | null => {
  if (!src) return null;

  try {
    const url = new URL(src, window.location.origin);
    const host = url.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (url.pathname === '/watch') {
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }

      if (url.pathname.startsWith('/shorts/')) {
        const id = url.pathname.split('/').filter(Boolean)[1];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }

      if (url.pathname.startsWith('/embed/')) {
        const id = url.pathname.split('/').filter(Boolean)[1];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const segments = url.pathname.split('/').filter(Boolean);
      const id = segments[segments.length - 1];
      if (/^\d+$/.test(id ?? '')) {
        return `https://player.vimeo.com/video/${id}`;
      }
    }
  } catch {
    return null;
  }

  return null;
};

const sanitizeStyleAttribute = (value: string): string | null => {
  const safeDeclarations = value
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .filter(declaration => {
      const colonIndex = declaration.indexOf(':');
      if (colonIndex === -1) return false;
      const property = declaration.slice(0, colonIndex).trim().toLowerCase();
      const propertyValue = declaration
        .slice(colonIndex + 1)
        .trim()
        .toLowerCase();
      if (!ALLOWED_STYLE_PROPS.has(property)) return false;
      if (propertyValue.includes('url(')) return false;
      return true;
    });

  return safeDeclarations.length > 0 ? safeDeclarations.join('; ') : null;
};

const sanitizeClassAttribute = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed && SAFE_CLASS_PATTERN.test(trimmed) ? trimmed : null;
};

const isAllowedAttribute = (tag: string, attrName: string): boolean => {
  const normalized = attrName.toLowerCase();
  if (normalized.startsWith('on')) return false;
  const pattern = ALLOWED_ATTRS[tag] ?? DEFAULT_ATTRS;
  return pattern.test(attrName);
};

export const RICH_TEXT_CONTENT_CLASS =
  'bn-rich-text max-w-none [&_img]:my-2 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg [&_iframe]:my-2 [&_iframe]:max-w-full [&_iframe]:rounded-lg [&_video]:my-2 [&_video]:h-auto [&_video]:max-w-full [&_video]:rounded-lg';

export const sanitizeRichTextHtml = (html: string): string => {
  if (!html || typeof window === 'undefined') return html ?? '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const walk = (parent: Element) => {
    const nodes = Array.from(parent.childNodes);

    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) continue;

      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove();
        continue;
      }

      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      let currentEl = el;

      if (tag === 'video') {
        const embedSrc = toEmbedVideoSrc(el.getAttribute('src') ?? '');
        if (embedSrc) {
          const iframe = doc.createElement('iframe');
          iframe.setAttribute('src', embedSrc);
          iframe.setAttribute('title', 'Embedded video');
          iframe.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
          );
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('frameborder', '0');

          const width = el.getAttribute('width');
          const height = el.getAttribute('height');
          if (width) {
            iframe.setAttribute('width', width);
          } else {
            iframe.setAttribute('width', '560');
          }
          if (height) {
            iframe.setAttribute('height', height);
          } else {
            iframe.setAttribute('height', '315');
          }

          el.parentNode?.replaceChild(iframe, el);
          currentEl = iframe;
        }
      }

      const currentTag = currentEl.tagName.toLowerCase();

      if (currentTag === 'input') {
        const inputType = (currentEl.getAttribute('type') ?? '').toLowerCase();
        if (inputType !== 'checkbox') {
          currentEl.remove();
          continue;
        }
      }

      if (!ALLOWED_TAGS.test(currentTag)) {
        while (currentEl.firstChild)
          currentEl.parentNode?.insertBefore(currentEl.firstChild, currentEl);
        currentEl.remove();
        continue;
      }

      for (const attr of Array.from(currentEl.attributes)) {
        const attrName = attr.name.toLowerCase();
        const value = attr.value.trim().toLowerCase();

        if (!isAllowedAttribute(currentTag, attr.name)) {
          currentEl.removeAttribute(attr.name);
          continue;
        }

        if (attrName === 'style') {
          const sanitizedStyle = sanitizeStyleAttribute(attr.value);
          if (sanitizedStyle) {
            currentEl.setAttribute('style', sanitizedStyle);
          } else {
            currentEl.removeAttribute('style');
          }
          continue;
        }

        if (attrName === 'class') {
          const sanitizedClass = sanitizeClassAttribute(attr.value);
          if (sanitizedClass) {
            currentEl.setAttribute('class', sanitizedClass);
          } else {
            currentEl.removeAttribute('class');
          }
          continue;
        }

        if (
          (attrName === 'href' || attrName === 'src' || attrName === 'action') &&
          value.startsWith('javascript:')
        ) {
          currentEl.removeAttribute(attr.name);
        }
      }

      if (currentTag === 'a') {
        const href = currentEl.getAttribute('href') ?? '';
        if (href.trim().toLowerCase().startsWith('javascript:')) {
          currentEl.removeAttribute('href');
        }
        const mapsEmbedSrc = normalizeGoogleMapsEmbedUrl(href);
        if (mapsEmbedSrc) {
          const iframe = doc.createElement('iframe');
          iframe.setAttribute('src', mapsEmbedSrc);
          iframe.setAttribute('title', 'Store location map');
          iframe.setAttribute('loading', 'lazy');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
          iframe.setAttribute('width', '100%');
          iframe.setAttribute('height', '420');
          currentEl.parentNode?.replaceChild(iframe, currentEl);
          walk(iframe);
          continue;
        }
        if (currentEl.getAttribute('target') === '_blank') {
          currentEl.setAttribute('rel', 'noopener noreferrer');
        }
      }

      if (currentTag === 'video' && !currentEl.hasAttribute('controls')) {
        currentEl.setAttribute('controls', '');
      }

      if (currentTag === 'iframe') {
        const src = currentEl.getAttribute('src') ?? '';
        try {
          const origin = new URL(src).origin;
          const isAllowedVideoOrigin = ALLOWED_IFRAME_ORIGINS.includes(origin);
          const isGoogleMapsEmbed = normalizeGoogleMapsEmbedUrl(src) !== null;
          if (!isAllowedVideoOrigin && !isGoogleMapsEmbed) {
            currentEl.remove();
            continue;
          }
        } catch {
          currentEl.remove();
          continue;
        }
      }

      walk(currentEl);
    }
  };

  walk(body);
  return body.innerHTML;
};
